import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toLocalISODate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { 
  AlertTriangle, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Plus,
  FileText,
  User,
  Calendar,
  Edit
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Incidents = () => {
  const { user, isAdmin, isSuperadmin } = useAuth();
  const { language } = useLanguage();
  
  const [incidents, setIncidents] = useState([]);
  const [timeRecords, setTimeRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [filter, setFilter] = useState('all');
  
  // Form state
  const [form, setForm] = useState({
    time_record_id: '',
    incident_type: '',
    reason: '',
    proposed_timestamp: '',
    proposed_record_type: ''
  });
  
  const [resolveForm, setResolveForm] = useState({
    status: '',
    admin_notes: ''
  });

  const incidentTypes = [
    { value: 'forgotten_clock', label: language === 'es' ? 'Olvido de fichaje' : 'Forgotten clock' },
    { value: 'wrong_type', label: language === 'es' ? 'Error de tipo (entrada/salida)' : 'Wrong type (in/out)' },
    { value: 'duplicate', label: language === 'es' ? 'Fichaje duplicado' : 'Duplicate record' },
    { value: 'time_correction', label: language === 'es' ? 'Corrección de hora' : 'Time correction' },
    { value: 'other', label: language === 'es' ? 'Otro' : 'Other' }
  ];

  const recordTypes = [
    { value: 'clock_in', label: language === 'es' ? 'Entrada' : 'Clock In' },
    { value: 'clock_out', label: language === 'es' ? 'Salida' : 'Clock Out' },
    { value: 'break_start', label: language === 'es' ? 'Inicio descanso' : 'Break Start' },
    { value: 'break_end', label: language === 'es' ? 'Fin descanso' : 'Break End' }
  ];

  useEffect(() => {
    fetchIncidents();
    fetchMyTimeRecords();
  }, [filter]);

  const fetchIncidents = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API_URL}/incidents${params}`);
      setIncidents(response.data);
    } catch (error) {
      console.error('Error fetching incidents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyTimeRecords = async () => {
    try {
      const today = new Date();
      const startDate = toLocalISODate(new Date(today.getFullYear(), today.getMonth() - 1, 1));
      const endDate = toLocalISODate(today);
      const response = await axios.get(`${API_URL}/time-records?start_date=${startDate}&end_date=${endDate}`);
      setTimeRecords(response.data);
    } catch (error) {
      console.error('Error fetching time records:', error);
    }
  };

  const handleCreateIncident = async () => {
    try {
      const payload = {
        incident_type: form.incident_type,
        reason: form.reason
      };
      
      if (form.time_record_id) {
        payload.time_record_id = form.time_record_id;
      }
      
      if (form.proposed_timestamp) {
        // Treat datetime-local input as Madrid time and convert to UTC ISO string
        const asIfUtc = new Date(form.proposed_timestamp + ':00Z');
        const madridStr = asIfUtc.toLocaleString('sv-SE', { timeZone: 'Europe/Madrid' });
        const madridAsUtc = new Date(madridStr.replace(' ', 'T') + 'Z');
        const offsetMs = asIfUtc.getTime() - madridAsUtc.getTime();
        payload.proposed_timestamp = new Date(asIfUtc.getTime() + offsetMs).toISOString();
      }
      
      if (form.proposed_record_type) {
        payload.proposed_record_type = form.proposed_record_type;
      }
      
      await axios.post(`${API_URL}/incidents`, payload);
      toast.success(language === 'es' ? 'Incidencia creada correctamente' : 'Incident created successfully');
      setShowCreateDialog(false);
      setForm({ time_record_id: '', incident_type: '', reason: '', proposed_timestamp: '', proposed_record_type: '' });
      fetchIncidents();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al crear incidencia' : 'Error creating incident'));
    }
  };

  const handleResolveIncident = async () => {
    try {
      await axios.put(`${API_URL}/incidents/${selectedIncident.id}/resolve`, resolveForm);
      toast.success(language === 'es' 
        ? `Incidencia ${resolveForm.status === 'approved' ? 'aprobada' : 'rechazada'}` 
        : `Incident ${resolveForm.status}`);
      setShowResolveDialog(false);
      setSelectedIncident(null);
      setResolveForm({ status: '', admin_notes: '' });
      fetchIncidents();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al resolver incidencia' : 'Error resolving incident'));
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-amber-100 text-amber-800', label: language === 'es' ? 'Pendiente' : 'Pending' },
      approved: { color: 'bg-green-100 text-green-800', label: language === 'es' ? 'Aprobada' : 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', label: language === 'es' ? 'Rechazada' : 'Rejected' }
    };
    const { color, label } = config[status] || config.pending;
    return <Badge className={color}>{label}</Badge>;
  };

  const getIncidentTypeLabel = (type) => {
    const found = incidentTypes.find(t => t.value === type);
    return found ? found.label : type;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    try {
      return new Date(timestamp).toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
        timeZone: 'Europe/Madrid',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  const canResolve = isAdmin() || isSuperadmin();

  return (
    <div className="space-y-6" data-testid="incidents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
            {language === 'es' ? 'Incidencias' : 'Incidents'}
          </h1>
          <p className="text-slate-500">
            {language === 'es' 
              ? 'Gestiona las incidencias de fichaje' 
              : 'Manage time record incidents'}
          </p>
        </div>
        
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'es' ? 'Todas' : 'All'}</SelectItem>
              <SelectItem value="pending">{language === 'es' ? 'Pendientes' : 'Pending'}</SelectItem>
              <SelectItem value="approved">{language === 'es' ? 'Aprobadas' : 'Approved'}</SelectItem>
              <SelectItem value="rejected">{language === 'es' ? 'Rechazadas' : 'Rejected'}</SelectItem>
            </SelectContent>
          </Select>
          
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-incident-btn">
            <Plus className="w-4 h-4 mr-2" />
            {language === 'es' ? 'Nueva Incidencia' : 'New Incident'}
          </Button>
        </div>
      </div>

      {/* Incidents List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            {language === 'es' ? 'Lista de Incidencias' : 'Incidents List'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : incidents.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              {language === 'es' ? 'No hay incidencias' : 'No incidents found'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{language === 'es' ? 'ID' : 'ID'}</TableHead>
                  <TableHead>{language === 'es' ? 'Fecha' : 'Date'}</TableHead>
                  <TableHead>{language === 'es' ? 'Empleado' : 'Employee'}</TableHead>
                  <TableHead>{language === 'es' ? 'Tipo' : 'Type'}</TableHead>
                  <TableHead>{language === 'es' ? 'Motivo' : 'Reason'}</TableHead>
                  <TableHead>{language === 'es' ? 'Estado' : 'Status'}</TableHead>
                  <TableHead>{language === 'es' ? 'Acciones' : 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {incidents.map((incident) => (
                  <TableRow key={incident.id}>
                    <TableCell className="font-mono text-xs text-slate-500">{incident.id?.slice(0, 8)}...</TableCell>
                    <TableCell>{formatDateTime(incident.requested_at)}</TableCell>
                    <TableCell>{incident.employee_name}</TableCell>
                    <TableCell>{getIncidentTypeLabel(incident.incident_type)}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{incident.reason}</TableCell>
                    <TableCell>{getStatusBadge(incident.status)}</TableCell>
                    <TableCell>
                      {incident.status === 'pending' && canResolve && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedIncident(incident);
                            setShowResolveDialog(true);
                          }}
                          data-testid={`resolve-incident-${incident.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          {language === 'es' ? 'Resolver' : 'Resolve'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Incident Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {language === 'es' ? 'Nueva Incidencia' : 'New Incident'}
            </DialogTitle>
            <DialogDescription>
              {language === 'es' 
                ? 'Reporta un error en tu fichaje para que sea revisado por un administrador'
                : 'Report a time record error to be reviewed by an administrator'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Tipo de incidencia' : 'Incident type'} *</Label>
              <Select
                value={form.incident_type}
                onValueChange={(v) => setForm({...form, incident_type: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder={language === 'es' ? 'Seleccionar tipo' : 'Select type'} />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  {incidentTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {form.incident_type !== 'forgotten_clock' && (
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Fichaje afectado' : 'Affected record'}</Label>
                <Select
                  value={form.time_record_id}
                  onValueChange={(v) => setForm({...form, time_record_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'es' ? 'Seleccionar fichaje' : 'Select record'} />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4}>
                    {timeRecords.map(record => (
                      <SelectItem key={record.id} value={record.id}>
                        {formatDateTime(record.timestamp)} - {record.record_type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {(form.incident_type === 'forgotten_clock' || form.incident_type === 'time_correction') && (
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Hora propuesta' : 'Proposed time'}</Label>
                <Input
                  type="datetime-local"
                  value={form.proposed_timestamp}
                  onChange={(e) => setForm({...form, proposed_timestamp: e.target.value})}
                />
              </div>
            )}
            
            {(form.incident_type === 'forgotten_clock' || form.incident_type === 'wrong_type') && (
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Tipo de fichaje correcto' : 'Correct record type'}</Label>
                <Select
                  value={form.proposed_record_type}
                  onValueChange={(v) => setForm({...form, proposed_record_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'es' ? 'Seleccionar tipo' : 'Select type'} />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4}>
                    {recordTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Motivo / Justificante' : 'Reason / Justification'} *</Label>
              <Textarea
                value={form.reason}
                onChange={(e) => setForm({...form, reason: e.target.value})}
                placeholder={language === 'es' 
                  ? 'Describe el motivo de la incidencia...'
                  : 'Describe the reason for the incident...'}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreateIncident}
              disabled={!form.incident_type || !form.reason}
            >
              {language === 'es' ? 'Crear Incidencia' : 'Create Incident'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Incident Dialog */}
      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {language === 'es' ? 'Resolver Incidencia' : 'Resolve Incident'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedIncident && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="font-medium">{selectedIncident.employee_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span>{getIncidentTypeLabel(selectedIncident.incident_type)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-600">{selectedIncident.reason}</span>
                </div>
                {selectedIncident.proposed_timestamp && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span className="text-sm">
                      {language === 'es' ? 'Hora propuesta:' : 'Proposed time:'} {formatDateTime(selectedIncident.proposed_timestamp)}
                    </span>
                  </div>
                )}
                {selectedIncident.original_record_snapshot && (
                  <div className="mt-2 p-2 bg-white rounded border text-sm">
                    <div className="font-medium text-slate-700 mb-1">
                      {language === 'es' ? 'Fichaje original:' : 'Original record:'}
                    </div>
                    <div>{formatDateTime(selectedIncident.original_record_snapshot.timestamp)} - {selectedIncident.original_record_snapshot.record_type}</div>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Decisión' : 'Decision'} *</Label>
                <div className="flex gap-2">
                  <Button
                    variant={resolveForm.status === 'approved' ? 'default' : 'outline'}
                    className={resolveForm.status === 'approved' ? 'bg-green-600 hover:bg-green-700' : ''}
                    onClick={() => setResolveForm({...resolveForm, status: 'approved'})}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Aprobar' : 'Approve'}
                  </Button>
                  <Button
                    variant={resolveForm.status === 'rejected' ? 'default' : 'outline'}
                    className={resolveForm.status === 'rejected' ? 'bg-red-600 hover:bg-red-700' : ''}
                    onClick={() => setResolveForm({...resolveForm, status: 'rejected'})}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Rechazar' : 'Reject'}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'es' ? 'Notas del administrador' : 'Admin notes'}</Label>
                <Textarea
                  value={resolveForm.admin_notes}
                  onChange={(e) => setResolveForm({...resolveForm, admin_notes: e.target.value})}
                  placeholder={language === 'es' 
                    ? 'Notas sobre la resolución...'
                    : 'Notes about the resolution...'}
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveDialog(false)}>
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleResolveIncident}
              disabled={!resolveForm.status}
            >
              {language === 'es' ? 'Confirmar' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Incidents;
